import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/data/keys';
import { deleteMyAccount, getProfile, updateProfile } from '@/lib/data/provider';
import type { Tables } from '@/lib/supabase/types';

export function useProfile() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: queryKeys.profile(userId ?? 'anon'),
    queryFn: () => getProfile(userId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useUpdateProfile() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      patch: Partial<
        Pick<
          Tables<'profiles'>,
          | 'display_name'
          | 'locale'
          | 'prefers_arabic_numerals'
          | 'favorite_team_id'
          | 'avatar_url'
          | 'display_name_confirmed'
        >
      >,
    ) => updateProfile(userId as string, patch),
    // Patch the cache SYNCHRONOUSLY before any post-success navigation. The
    // onboarding finalize flips display_name_confirmed and immediately routes to
    // the tabs; the tabs gate re-reads this profile, so an async-only
    // invalidate would let the gate see a stale `false` and bounce the user back
    // into the picker (the "redo / doesn't save" loop). setQueryData closes that
    // race; invalidate then reconciles with the server.
    onSuccess: (_data, patch) => {
      if (!userId) return;
      // If the profile isn't cached yet (first load), seed it with the patch so the
      // tabs gate sees display_name_confirmed=true immediately; the invalidate below
      // reconciles the rest of the row from the server right after.
      queryClient.setQueryData<Tables<'profiles'>>(queryKeys.profile(userId), (old) =>
        old ? { ...old, ...patch } : ({ ...patch } as Tables<'profiles'>),
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.profile(userId) });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMyAccount,
    onSuccess: () => {
      queryClient.clear();
    },
  });
}
